import { BayCardState } from "../../utils/bayCardState";

// English labels, passed through t() by the caller.
export const bayStateLabel = (state: BayCardState): string => {
  switch (state) {
    case "over":
      return "Over target";
    case "warning":
      return "Near target";
    case "waiting":
      return "Ready to move on";
    case "paused":
      return "Paused";
    case "working":
      return "Working";
    case "notStarted":
      return "Not started";
    default:
      return "Idle";
  }
};

interface BayStateClasses {
  pill: string;
  dot: string;
  card: string;
  timer: string;
  panel: string;
  note: string;
  step: string;
}

// Same palette the flow lanes and AssemblyTimer already use: emerald running, amber warning, red
// over, blue for "finished here, move it on", grey for stopped.
export const BAY_STATE_LIGHT: Record<BayCardState, BayStateClasses> = {
  working: {
    pill: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    dot: "bg-emerald-500",
    card: "bg-white border-gray-200",
    timer: "text-emerald-700",
    panel: "bg-emerald-50",
    note: "text-gray-600",
    step: "bg-emerald-500",
  },
  warning: {
    pill: "bg-amber-50 text-amber-700 border border-amber-200",
    dot: "bg-amber-500",
    card: "bg-white border-amber-300",
    timer: "text-amber-700",
    panel: "bg-amber-50",
    note: "text-amber-700",
    step: "bg-amber-500",
  },
  over: {
    pill: "bg-red-50 text-red-700 border border-red-200",
    dot: "bg-red-500",
    card: "bg-white border-red-300 ring-2 ring-red-100",
    timer: "text-red-700",
    panel: "bg-red-50",
    note: "text-red-700",
    step: "bg-red-500",
  },
  waiting: {
    pill: "bg-blue-100 text-blue-700 border border-blue-300",
    dot: "bg-blue-500",
    card: "bg-blue-50 border-blue-300",
    timer: "text-blue-700",
    panel: "bg-blue-100",
    note: "text-blue-700",
    step: "bg-blue-500",
  },
  paused: {
    pill: "bg-gray-100 text-gray-600 border border-gray-300",
    dot: "bg-gray-400",
    card: "bg-white border-gray-200",
    timer: "text-gray-600",
    panel: "bg-gray-100",
    note: "text-gray-600",
    step: "bg-gray-400",
  },
  notStarted: {
    pill: "bg-gray-50 text-gray-600 border border-gray-200",
    dot: "bg-gray-300",
    card: "bg-white border-gray-200",
    timer: "text-gray-500",
    panel: "bg-gray-50",
    note: "text-gray-500",
    step: "bg-gray-300",
  },
  idle: {
    pill: "bg-white text-gray-500 border border-gray-300",
    dot: "bg-gray-300",
    card: "bg-gray-50 border-dashed border-gray-300",
    timer: "text-gray-500",
    panel: "bg-gray-50",
    note: "text-gray-500",
    step: "bg-gray-200",
  },
};

// TV mode sits on a near-black ground, so the tints go dark and the text goes light.
export const BAY_STATE_DARK: Record<BayCardState, BayStateClasses> = {
  working: {
    pill: "bg-emerald-900 text-emerald-200",
    dot: "bg-emerald-400",
    card: "bg-gray-900 border-gray-800",
    timer: "text-emerald-400",
    panel: "",
    note: "text-gray-400",
    step: "bg-emerald-400",
  },
  warning: {
    pill: "bg-amber-900 text-amber-200",
    dot: "bg-amber-400",
    card: "bg-amber-950 border-amber-600",
    timer: "text-amber-400",
    panel: "",
    note: "text-amber-300",
    step: "bg-amber-400",
  },
  over: {
    pill: "bg-red-900 text-red-200",
    dot: "bg-red-400",
    card: "bg-red-950 border-red-500",
    timer: "text-red-400",
    panel: "",
    note: "text-red-300",
    step: "bg-red-400",
  },
  waiting: {
    pill: "bg-blue-900 text-blue-200",
    dot: "bg-blue-400",
    card: "bg-blue-950 border-blue-500",
    timer: "text-blue-300",
    panel: "",
    note: "text-blue-300",
    step: "bg-blue-400",
  },
  paused: {
    pill: "bg-gray-700 text-gray-200",
    dot: "bg-gray-400",
    card: "bg-gray-900 border-gray-700",
    timer: "text-gray-400",
    panel: "",
    note: "text-gray-400",
    step: "bg-gray-500",
  },
  notStarted: {
    pill: "bg-gray-800 text-gray-300",
    dot: "bg-gray-500",
    card: "bg-gray-900 border-gray-800",
    timer: "text-gray-400",
    panel: "",
    note: "text-gray-400",
    step: "bg-gray-600",
  },
  idle: {
    pill: "bg-transparent text-gray-400 border border-gray-600",
    dot: "bg-gray-600",
    card: "bg-transparent border-dashed border-gray-700",
    timer: "text-gray-400",
    panel: "",
    note: "text-gray-400",
    step: "bg-gray-800",
  },
};
